import { useUpdateOrderQuoteMutation } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { EMPTY_ADDRESS, type AddressData } from '../../ui/address-form';
import { AddressPicker } from '../../ui/address-picker';
import { Button } from '../../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';

interface EditOrderQuoteAddressModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	quoteId: string;
	/** The client whose saved addresses are offered alongside the custom form. */
	organizationId?: string | null;
	initialValue: {
		addressLine1?: string | null;
		addressLine2?: string | null;
		city?: string | null;
		stateCode?: string | null;
		postalCode?: string | null;
		countryCode?: string | null;
	} | null;
	onSuccess: () => void;
}

export function EditOrderQuoteAddressModal({
	open,
	onOpenChange,
	quoteId,
	organizationId,
	initialValue,
	onSuccess,
}: EditOrderQuoteAddressModalProps) {
	const [address, setAddress] = useState<AddressData>(EMPTY_ADDRESS);
	const [updateQuote, { loading }] = useUpdateOrderQuoteMutation();

	useEffect(() => {
		if (open) {
			setAddress({
				addressLine1: initialValue?.addressLine1 || '',
				addressLine2: initialValue?.addressLine2 || '',
				city: initialValue?.city || '',
				stateCode: initialValue?.stateCode || '',
				postalCode: initialValue?.postalCode || '',
				countryCode: initialValue?.countryCode || '',
				googlePlaceId: '',
				latitude: null,
				longitude: null,
			});
		}
	}, [open, initialValue]);

	const handleSubmit = async () => {
		try {
			await updateQuote({
				variables: {
					id: quoteId,
					input: {
						deliveryAddress: address.addressLine1
							? {
									addressLine1: address.addressLine1,
									addressLine2: address.addressLine2 || undefined,
									city: address.city || undefined,
									stateCode: address.stateCode || undefined,
									postalCode: address.postalCode || undefined,
									countryCode: address.countryCode || undefined,
									googlePlaceId: address.googlePlaceId || undefined,
									latitude: address.latitude,
									longitude: address.longitude,
								}
							: undefined,
					},
				},
			});
			toast.success('Address updated');
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Edit delivery address</DialogTitle>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						{/* Editing keeps whatever the quote already holds — the picker
						    only offers the client's saved addresses beside it. */}
						<AddressPicker
							organizationId={organizationId}
							value={address}
							onChange={setAddress}
							skip={!open}
							autoSelectPrimary={false}
						/>
					</DialogBody>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button type="submit" disabled={loading}>
							{loading ? 'Saving…' : 'Save'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
